/**
 * LEGACY rectification engine (pre-KP era). NOT the live scoring path.
 *
 * Port of the subset of astro_calculations/birthtime_rectification.py that
 * lib/event_model.py re-exports, plus score_birth_time's internal dependency
 * closure. The Python module header explains the split:
 *
 *   The live deterministic engine lives entirely in lib/ (KP signed-significator +
 *   traits + strength/Ashtakavarga gates), driven from streamlit_app.py. This module
 *   is retained ONLY for:
 *     • pure shared primitives the live engine still uses — LifeEvent, weighted_mean,
 *       targets_from_category, dasha_timing_defaults, moon_nakshatra_pada;
 *     • score_birth_time, a fallback scorer reached only when the KP engine returns
 *       no score;
 *     • the standalone CLI in __main__.   [CLI / CSV / pandas loading NOT ported]
 *
 *   DO NOT add new scoring logic here — build it in lib/. The category tables here
 *   (CATEGORY_TARGETS / TargetRule) belong to the legacy metric scorer and are
 *   deliberately separate from the live engine's KP tables in lib/kp_scoring.py.
 *
 * score_birth_time's closure pulls in static_predictions.get_predictions (see
 * static_predictions.ts), which in turn drives transit_data.TransitData — the
 * legacy Equal-house chart builder — so a "minimal" port still spans those modules.
 */

import { mean } from "../lib/pyutil";
import { pythonRound } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import {
  addDays,
  addSeconds,
  combine,
  dateOnly,
  datesEqual,
  diffSeconds,
  formatISODate,
  formatISODateTime,
  naiveSeconds,
  plainDateTime,
} from "../lib/datetime";
import { pyfloordiv } from "./pyfloat";
import { calculate_chart, local_to_jd } from "./chart";
import { get_config } from "./config";
import { get_current_dasha, init_dasha_jd } from "./dasha";
import { get_predictions } from "./static_predictions";

export const METRIC_COLUMNS = [
  "health",
  "mood",
  "relationship",
  "career",
  "financial",
  "learning",
  "parents",
  "children",
  "hardship",
];

export const METRIC_TO_PREDICTION_KEYS: Record<string, string[]> = {
  health: ["D1_Health", "D45_Wellbeing", "D8_Longevity"],
  mood: ["D1_Mood", "D1_Confidence", "D45_Wellbeing"],
  relationship: ["D1_Relationships", "D9_Relationships"],
  career: ["D1_Career", "D10_Career", "D5_Authority"],
  financial: ["D1_Finance", "D2_Wealth"],
  learning: ["D1_LearningIdeas", "D24_Education"],
  parents: ["D12_Parents"],
  children: ["D7_Children"],
  hardship: ["D30_Difficulties", "D1_Transformation", "D1_Conflict"],
};

export const PREDICTION_WEIGHTS: Record<string, number[]> = {
  health: [0.5, 0.3, 0.2],
  mood: [0.6, 0.2, 0.2],
  relationship: [0.5, 0.5],
  career: [0.4, 0.4, 0.2],
  financial: [0.5, 0.5],
  learning: [0.6, 0.4],
  parents: [1.0],
  children: [1.0],
  hardship: [0.4, 0.4, 0.2],
};

export const DASHA_TIMING_DEFAULTS: Record<string, [number, number]> = {
  award: [0.25, 45.0],
  career_breakthrough: [0.3, 60.0],
  career_contract: [0.3, 60.0],
  career_failure: [0.25, 90.0],
  child_birth: [0.0, 60.0],
  death_family: [0.25, 60.0],
  education_start: [0.2, 60.0],
  injury: [0.15, 45.0],
  marriage: [0.35, 30.0],
  relationship_conflict: [0.25, 90.0],
};
export const DASHA_SCORE_CAP = 25.0;

export interface TargetRule {
  metric: string;
  coefficient: number;
  weight: number; // default 1.0
}

function rule(metric: string, coefficient: number, weight = 1.0): TargetRule {
  return { metric, coefficient, weight };
}

export const CATEGORY_TARGETS: Record<string, TargetRule[]> = {
  award: [
    rule("D5_Authority", 1.1, 1.0),
    rule("D10_Career", 1.0, 0.9),
    rule("D1_Career", 0.9, 0.8),
    rule("D1_Confidence", 0.6, 0.5),
  ],
  career_breakthrough: [
    rule("D10_Career", 1.1, 1.0),
    rule("D1_Career", 1.0, 0.9),
    rule("D5_Authority", 0.7, 0.6),
    rule("D1_Confidence", 0.7, 0.5),
  ],
  career_contract: [
    rule("D10_Career", 1.1, 1.0),
    rule("D1_Career", 1.0, 0.9),
    rule("D2_Wealth", 0.8, 0.7),
    rule("D1_Confidence", 0.6, 0.5),
  ],
  career_failure: [
    rule("D1_Career", 1.0, 1.0),
    rule("D10_Career", 0.9, 0.8),
    rule("D1_Finance", 0.9, 0.8),
    rule("D1_Confidence", 0.7, 0.6),
    rule("D1_Conflict", 0.8, 0.7),
    rule("D30_Difficulties", 1.0, 1.0),
  ],
  career_learning: [
    rule("D1_LearningIdeas", 1.0, 1.0),
    rule("D24_Education", 0.9, 0.9),
    rule("D1_Career", 0.6, 0.5),
    rule("D10_Career", 0.6, 0.5),
    rule("D1_Drive", 0.6, 0.4),
  ],
  child_birth: [rule("D7_Children", 1.2, 1.0)],
  death_family: [
    rule("D12_Parents", 1.0, 1.0),
    rule("D1_Mood", 0.8, 0.7),
    rule("D30_Difficulties", 0.7, 0.7),
    rule("D1_Transformation", 0.7, 0.5),
  ],
  education_start: [
    rule("D24_Education", 1.1, 1.0),
    rule("D1_LearningIdeas", 0.9, 0.8),
    rule("D1_Confidence", 0.5, 0.4),
    rule("D1_Relationships", 0.4, 0.3),
  ],
  injury: [
    rule("D1_Health", 1.0, 1.0),
    rule("D30_Difficulties", 0.9, 0.9),
    rule("D8_Longevity", 0.5, 0.4),
    rule("D1_Mood", 0.4, 0.3),
  ],
  intimacy_relationship: [
    rule("D1_Relationships", 0.9, 1.0),
    rule("D9_Relationships", 0.8, 0.8),
    rule("D1_Mood", 0.4, 0.3),
  ],
  marriage: [rule("D9_Relationships", 1.2, 1.0)],
  mood_low: [rule("D1_Mood", 1.0, 1.0), rule("D45_Wellbeing", 0.7, 0.7)],
  pet_bond: [
    rule("D1_Mood", 0.7, 0.7),
    rule("D1_Relationships", 0.5, 0.5),
    rule("D9_Relationships", 0.3, 0.3),
  ],
  pet_death: [
    rule("D1_Mood", 0.9, 1.0),
    rule("D45_Wellbeing", 0.6, 0.6),
    rule("D30_Difficulties", 0.4, 0.4),
  ],
  relationship_conflict: [
    rule("D1_Relationships", 1.0, 1.0),
    rule("D9_Relationships", 0.8, 0.7),
    rule("D1_Mood", 0.7, 0.6),
    rule("D1_Conflict", 1.0, 1.0),
    rule("D30_Difficulties", 0.8, 0.8),
  ],
  travel_family: [
    rule("D1_Relationships", 0.6, 0.7),
    rule("D1_Mood", 0.6, 0.6),
    rule("D4_Fortune", 0.5, 0.5),
    rule("D9_Relationships", 0.4, 0.4),
  ],
  anxiety_health: [
    rule("D1_Mood", 1.0, 1.0),
    rule("D45_Wellbeing", 0.9, 0.9),
    rule("D1_Health", 0.7, 0.7),
    rule("D1_Confidence", 0.6, 0.5),
    rule("D1_Conflict", 0.6, 0.5),
    rule("D30_Difficulties", 0.8, 0.8),
  ],
  adventure_positive: [
    rule("D1_Drive", 0.9, 0.9),
    rule("D1_Mood", 0.7, 0.7),
    rule("D1_Confidence", 0.7, 0.6),
    rule("D30_Difficulties", 0.3, 0.3),
  ],
  social_celebration: [
    rule("D1_Relationships", 0.8, 0.8),
    rule("D9_Relationships", 0.7, 0.7),
    rule("D1_Mood", 0.7, 0.7),
    rule("D30_Difficulties", 0.3, 0.3),
  ],
};

export interface LifeEvent {
  date: PlainDateTime;
  label: string;
  expected_ranges: Record<string, [number, number]>;
  raw_values: Record<string, number>;
  metric_weights: Record<string, number>;
  category: string;
  polarity: number;
  severity: number;
  confidence: number;
  weight: number;
  date_accuracy_days: number;
  has_exact_time: boolean;
  event_lat: number | null;
  event_lon: number | null;
  dasha_timing_weight: number;
  dasha_timing_window_days: number;
  start_date: PlainDateTime | null;
  end_date: PlainDateTime | null;
}

/** Factory with the Python dataclass defaults. */
export function createLifeEvent(
  init: Partial<LifeEvent> & Pick<LifeEvent, "date" | "label" | "expected_ranges" | "raw_values">
): LifeEvent {
  return {
    date: init.date,
    label: init.label,
    expected_ranges: init.expected_ranges,
    raw_values: init.raw_values,
    metric_weights: init.metric_weights ?? {},
    category: init.category ?? "",
    polarity: init.polarity ?? 1,
    severity: init.severity ?? 3.0,
    confidence: init.confidence ?? 1.0,
    weight: init.weight ?? 1.0,
    date_accuracy_days: init.date_accuracy_days ?? 0.0,
    has_exact_time: init.has_exact_time ?? false,
    event_lat: init.event_lat ?? null,
    event_lon: init.event_lon ?? null,
    dasha_timing_weight: init.dasha_timing_weight ?? 0.0,
    dasha_timing_window_days: init.dasha_timing_window_days ?? 60.0,
    start_date: init.start_date ?? null,
    end_date: init.end_date ?? null,
  };
}

export function dasha_timing_defaults(category: string): [number, number] {
  return DASHA_TIMING_DEFAULTS[category] ?? [0.0, 60.0];
}

export function expected_range(value: number, bottom: number, top: number): [number, number] {
  if (value >= 0) {
    return [value - bottom, value + top];
  }
  return [value - top, value + bottom];
}

export function weighted_mean(values: Array<[number, number]>): number {
  let total_weight = 0.0;
  for (const [, weight] of values) {
    if (weight > 0) total_weight += weight;
  }
  if (total_weight <= 0) {
    return 0.0;
  }
  let total = 0.0;
  for (const [value, weight] of values) {
    if (weight > 0) total += value * weight;
  }
  return total / total_weight;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function targets_from_category(
  category: string,
  polarity: number,
  severity: number
): [Record<string, [number, number]>, Record<string, number>, Record<string, number>] {
  const rules = CATEGORY_TARGETS[category] ?? [];
  const ranges: Record<string, [number, number]> = {};
  const raw_values: Record<string, number> = {};
  const metric_weights: Record<string, number> = {};

  for (const rule of rules) {
    const target = clamp(severity * polarity * rule.coefficient, -5.0, 5.0);
    const tolerance = Math.max(0.75, 1.6 - rule.weight * 0.4);
    ranges[rule.metric] = [target - tolerance, target + tolerance];
    raw_values[rule.metric] = target;
    metric_weights[rule.metric] = rule.weight;
  }

  return [ranges, raw_values, metric_weights];
}

/**
 * 1-BASED nakshatra (1-27) and pada (1-4) — note _chart.get_nakshatra is 0-based.
 * Uses Python floor-division semantics (pyfloordiv): at exact-boundary longitudes
 * like 40.0 the fmod-based `//` lands on the lower nakshatra where JS
 * Math.floor(a / b) would round across the boundary.
 */
export function moon_nakshatra_pada(moon_lon: number | null): [number | null, number | null] {
  if (moon_lon === null) {
    return [null, null];
  }
  const nakshatra_size = 360.0 / 27.0;
  const pada_size = nakshatra_size / 4.0;
  const nakshatra = Math.trunc(pyfloordiv(moon_lon, nakshatra_size)) + 1;
  const pada = Math.trunc(pyfloordiv(moon_lon % nakshatra_size, pada_size)) + 1;
  return [nakshatra, pada];
}

export function score_single_metric(
  predicted: number,
  expected_min: number,
  expected_max: number,
  expected_value: number | null = null,
  scoring_method = "gaussian"
): number {
  const range_size = expected_max - expected_min;
  const center = (expected_min + expected_max) / 2;

  if (scoring_method === "gaussian") {
    const target = expected_value === null ? center : expected_value;
    const sigma = Math.max(range_size / 2, 0.5);
    const residual = (predicted - target) / sigma;
    return Math.max(0.0, 100.0 * Math.exp(-0.5 * residual * residual));
  }

  if (expected_min <= predicted && predicted <= expected_max) {
    const max_distance = range_size > 0 ? range_size / 2 : 1.0;
    const closeness = 1 - Math.abs(predicted - center) / max_distance;
    return 80 + 20 * Math.max(0.0, closeness);
  }

  const deviation = predicted < expected_min ? expected_min - predicted : predicted - expected_max;
  const normalized = range_size > 0 ? deviation / (range_size / 2 + 0.1) : deviation;
  return Math.max(0.0, 80.0 - normalized * 40.0);
}

export function combined_prediction(
  metric: string,
  predictions: Record<string, any>
): [number | null, string[]] {
  const direct_value = predictions[metric];
  if (typeof direct_value === "number") {
    return [direct_value, [metric]];
  }

  const keys = METRIC_TO_PREDICTION_KEYS[metric] ?? [];
  const weights = PREDICTION_WEIGHTS[metric] ?? [1.0];
  let total = 0.0;
  let total_weight = 0.0;
  const used_keys: string[] = [];

  for (let index = 0; index < keys.length; index++) {
    const key = keys[index];
    const value = predictions[key];
    if (typeof value !== "number") {
      continue;
    }
    const weight = index < weights.length ? weights[index] : weights[weights.length - 1];
    total += value * weight;
    total_weight += weight;
    used_keys.push(key);
  }

  if (total_weight === 0) {
    return [null, used_keys];
  }
  return [total / total_weight, used_keys];
}

export function score_event(
  predictions: Record<string, any>,
  event: LifeEvent,
  scoring_method: string
): [number, Record<string, any>] {
  const metric_scores: Array<[number, number]> = [];
  const details: Record<string, any> = {};

  for (const [metric, [expected_min, expected_max]] of Object.entries(event.expected_ranges)) {
    const [predicted, used_keys] = combined_prediction(metric, predictions);
    if (predicted === null) {
      continue;
    }

    const score = score_single_metric(
      predicted,
      expected_min,
      expected_max,
      event.raw_values[metric] ?? null,
      scoring_method
    );
    const metric_weight = event.metric_weights[metric] ?? 1.0;
    metric_scores.push([score, metric_weight]);
    details[metric] = {
      predicted: pythonRound(predicted, 3),
      expected: event.raw_values[metric] ?? null,
      expected_min: expected_min,
      expected_max: expected_max,
      score: pythonRound(score, 3),
      weight: metric_weight,
      in_range: expected_min <= predicted && predicted <= expected_max,
      prediction_keys: used_keys,
    };
  }

  return [weighted_mean(metric_scores), details];
}

/**
 * Python builds samples with timedelta arithmetic at microsecond resolution; the port
 * works in float seconds and floors fractional seconds when materialising a datetime.
 * Observable behaviour matches: Python's downstream consumers (local_to_jd, strftime)
 * ignore the microsecond component entirely.
 */
export function capped_time_window_samples(
  start: PlainDateTime,
  end: PlainDateTime,
  interval_hours: number,
  max_samples: number
): PlainDateTime[] {
  const interval_seconds = Math.max(1, Math.min(24, interval_hours)) * 3600;
  if (naiveSeconds(end) < naiveSeconds(start)) {
    [start, end] = [end, start];
  }

  const samples: PlainDateTime[] = [];
  let current = start;
  while (naiveSeconds(current) <= naiveSeconds(end)) {
    samples.push(current);
    current = addSeconds(current, interval_seconds);
    if (samples.length > max_samples) {
      break;
    }
  }

  if (samples.length <= max_samples) {
    return samples;
  }

  if (max_samples <= 1) {
    return [addSeconds(start, Math.floor(diffSeconds(end, start) / 2))];
  }

  const span_seconds = diffSeconds(end, start);
  const out: PlainDateTime[] = [];
  for (let index = 0; index < max_samples; index++) {
    out.push(addSeconds(start, Math.floor(span_seconds * (index / (max_samples - 1)))));
  }
  return out;
}

export function event_sample_datetimes(
  event: LifeEvent,
  event_mode: string,
  window_hours: number,
  max_event_samples: number
): PlainDateTime[] {
  if (["snapshot", "daily_summary"].includes(event_mode) || event.has_exact_time) {
    return [event.date];
  }

  if (event.start_date !== null && event.end_date !== null) {
    const is_date_range = !datesEqual(dateOnly(event.start_date), dateOnly(event.end_date));
    let start: PlainDateTime;
    let end: PlainDateTime;
    if (is_date_range) {
      start = combine(dateOnly(event.start_date), { hour: 0, minute: 0, second: 0 });
      end = combine(dateOnly(event.end_date), { hour: 23, minute: 59, second: 59 });
    } else {
      const uncertainty_days = Math.ceil(event.date_accuracy_days);
      start = combine(addDays(dateOnly(event.date), -uncertainty_days), { hour: 0, minute: 0, second: 0 });
      end = combine(addDays(dateOnly(event.date), uncertainty_days), { hour: 23, minute: 59, second: 59 });
    }
    return capped_time_window_samples(start, end, window_hours, Math.max(1, max_event_samples));
  }

  const uncertainty_days = Math.ceil(event.date_accuracy_days);
  const start = combine(addDays(dateOnly(event.date), -uncertainty_days), { hour: 0, minute: 0, second: 0 });
  const end = combine(addDays(dateOnly(event.date), uncertainty_days), { hour: 23, minute: 59, second: 59 });
  return capped_time_window_samples(start, end, window_hours, Math.max(1, max_event_samples));
}

export function aggregate_sample_scores(scores: number[], method: string): number {
  if (!scores.length) {
    return 0.0;
  }
  if (method === "best") {
    return Math.max(...scores);
  }
  return mean(scores);
}

export function gaussian_score_from_days(distance_days: number, sigma_days: number): number {
  const sigma = Math.max(1.0, sigma_days);
  const residual = distance_days / sigma;
  return Math.max(0.0, 100.0 * Math.exp(-0.5 * residual * residual));
}

export function score_dasha_timing(
  sample_datetime: PlainDateTime,
  birth_datetime: PlainDateTime,
  dasha_info: Record<string, number>,
  timezone_offset: number,
  window_days: number
): [number, Record<string, any>] {
  const dasha = get_current_dasha(sample_datetime, birth_datetime, dasha_info, timezone_offset);
  const current_jd = local_to_jd(sample_datetime, timezone_offset);
  const maha_distance = Math.min(
    Math.abs(current_jd - dasha.maha_start_jd),
    Math.abs(dasha.maha_end_jd - current_jd)
  );
  const antar_distance = Math.min(
    Math.abs(current_jd - dasha.antar_start_jd),
    Math.abs(dasha.antar_end_jd - current_jd)
  );
  const maha_score = gaussian_score_from_days(maha_distance, window_days);
  const antar_score = gaussian_score_from_days(antar_distance, window_days) * 0.75;
  const score = Math.max(maha_score, antar_score);
  return [
    score,
    {
      score: pythonRound(score, 3),
      mahadasha: dasha.mahadasha_lord,
      antardasha: dasha.antardasha_lord,
      nearest_maha_boundary_days: pythonRound(maha_distance, 3),
      nearest_antar_boundary_days: pythonRound(antar_distance, 3),
      maha_boundary_score: pythonRound(maha_score, 3),
      antar_boundary_score: pythonRound(antar_score, 3),
      window_days: window_days,
    },
  ];
}

export function score_birth_time(
  person: string,
  birth_time: [number, number, number],
  birth_data: Record<string, Record<string, any>>,
  events: LifeEvent[],
  timezone_offset: number,
  event_mode: string,
  window_hours: number,
  max_event_samples: number,
  window_aggregation: string,
  scoring_method: string
): [number, Array<Record<string, any>>, Array<Record<string, number>>] {
  const info = birth_data[person];
  const [hour, minute, second] = birth_time;
  const test_birth = plainDateTime(info["year"], info["month"], info["date"], hour, minute, second);
  let dasha_info: Record<string, number> | null = null;
  if (events.some((event) => event.dasha_timing_weight > 0)) {
    const natal_chart = calculate_chart(test_birth, info["lat"], info["lon"], timezone_offset, get_config());
    dasha_info = init_dasha_jd(natal_chart, natal_chart.julian_day);
  }

  const weighted_event_scores: Array<[number, number]> = [];
  const event_score_records: Array<Record<string, number>> = [];
  const event_details: Array<Record<string, any>> = [];

  for (const event of events) {
    const sample_scores: number[] = [];
    const sample_metric_scores: number[] = [];
    const sample_dasha_scores: number[] = [];
    const sample_details: Array<Record<string, any>> = [];
    const sample_mode = event_mode === "daily_summary" ? "daily_summary" : "snapshot";

    for (const sample_datetime of event_sample_datetimes(event, event_mode, window_hours, max_event_samples)) {
      const current_lat = event.event_lat !== null ? event.event_lat : info["lat"];
      const current_lon = event.event_lon !== null ? event.event_lon : info["lon"];
      const predictions = get_predictions(
        sample_datetime,
        person,
        current_lat,
        current_lon,
        timezone_offset,
        sample_mode,
        -1,
        test_birth,
        info
      );
      const [metric_score, details] = score_event(predictions, event, scoring_method);
      let sample_score = metric_score;
      let dasha_score = 0.0;
      let dasha_details: Record<string, any> | null = null;
      if (dasha_info !== null && event.dasha_timing_weight > 0) {
        [dasha_score, dasha_details] = score_dasha_timing(
          sample_datetime,
          test_birth,
          dasha_info,
          timezone_offset,
          event.dasha_timing_window_days
        );
        const dasha_lift = Math.min(Math.max(0.0, dasha_score - metric_score), DASHA_SCORE_CAP);
        sample_score = weighted_mean([
          [metric_score, 1.0],
          [metric_score + dasha_lift, event.dasha_timing_weight],
        ]);
      }

      sample_metric_scores.push(metric_score);
      sample_dasha_scores.push(dasha_score);
      sample_scores.push(sample_score);
      sample_details.push({
        datetime: formatISODateTime(sample_datetime),
        score: sample_score,
        metric_score: metric_score,
        dasha_timing_score: dasha_score,
        dasha_timing: dasha_details,
        metrics: details,
      });
    }

    const event_score = aggregate_sample_scores(sample_scores, window_aggregation);
    const metric_event_score = sample_metric_scores.length
      ? aggregate_sample_scores(sample_metric_scores, window_aggregation)
      : 0.0;
    const dasha_event_score = sample_dasha_scores.length
      ? aggregate_sample_scores(sample_dasha_scores, window_aggregation)
      : 0.0;
    let best_sample: Record<string, any> = {};
    if (sample_details.length) {
      best_sample = sample_details[0];
      for (const item of sample_details) {
        if (item["score"] > best_sample["score"]) best_sample = item;
      }
    }
    const effective_weight = event.weight * event.confidence;
    weighted_event_scores.push([event_score, effective_weight]);
    event_score_records.push({ score: event_score, weight: effective_weight });
    event_details.push({
      date: formatISODate(event.date),
      label: event.label,
      category: event.category,
      polarity: event.polarity,
      severity: event.severity,
      confidence: event.confidence,
      score: pythonRound(event_score, 3),
      metric_score: pythonRound(metric_event_score, 3),
      dasha_timing_score: pythonRound(dasha_event_score, 3),
      dasha_timing_weight: event.dasha_timing_weight,
      dasha_timing_window_days: event.dasha_timing_window_days,
      weight: event.weight,
      effective_weight: pythonRound(effective_weight, 3),
      date_accuracy_days: event.date_accuracy_days,
      has_exact_time: event.has_exact_time,
      event_lat: event.event_lat,
      event_lon: event.event_lon,
      sample_count: sample_scores.length,
      sample_score_min: sample_scores.length ? pythonRound(Math.min(...sample_scores), 3) : 0.0,
      sample_score_max: sample_scores.length ? pythonRound(Math.max(...sample_scores), 3) : 0.0,
      sample_score_mean: sample_scores.length ? pythonRound(mean(sample_scores), 3) : 0.0,
      best_sample_datetime: best_sample["datetime"] ?? null,
      best_sample_dasha_timing: best_sample["dasha_timing"] ?? null,
      raw_values: event.raw_values,
      metrics: best_sample["metrics"] ?? {},
    });
  }

  return [weighted_mean(weighted_event_scores), event_details, event_score_records];
}
